<template>
  <v-expansion-panel
    v-if="
      typeof geometryData !== 'undefined' && geometryData.class === 'source'
    "
  >
    <v-expansion-panel-header>Source Properties</v-expansion-panel-header>
    <v-expansion-panel-content>
      <section class="item-body">
        <v-col>
          <!-- 3D (NGSolve): fonte gaussiana localizada, polarizada em x -->
          <template v-if="is3d">
            <v-text-field
              :value="geometryData.waveLength"
              outlined
              dense
              type="number"
              label="Wavelength λ (µm)"
              hint="Wavelength in vacuum, e.g. 1.55 for telecom"
              persistent-hint
              @change="set('waveLength', $event)"
            />
            <v-text-field
              :value="geometryData.waveWidth"
              class="mt-3"
              outlined
              dense
              type="number"
              label="Source size σ (µm)"
              hint="Standard deviation of the Gaussian current. Keep it smaller than λ/2"
              persistent-hint
              @change="set('waveWidth', $event)"
            />
          </template>

          <template v-else>
            <v-select
              :value="geometryData.sourceType || 'continuous'"
              :items="sourceTypes"
              outlined
              dense
              label="Source type"
              :hint="typeHint"
              persistent-hint
              @change="set('sourceType', $event, false)"
            />

            <v-row dense class="mt-3">
              <v-col cols="6">
                <v-text-field
                  :value="geometryData.waveLength"
                  outlined
                  dense
                  type="number"
                  label="Wavelength λ (µm)"
                  :hint="frequencyHint"
                  persistent-hint
                  @change="setWavelength"
                />
              </v-col>
              <v-col cols="6">
                <v-select
                  :value="geometryData.component || 'Ez'"
                  :items="components"
                  outlined
                  dense
                  label="Polarization"
                  hint="Field component excited by the source"
                  persistent-hint
                  @change="set('component', $event, false)"
                />
              </v-col>
            </v-row>

            <v-text-field
              v-if="geometryData.sourceType === 'gaussian'"
              :value="geometryData.fwidth"
              class="mt-3"
              outlined
              dense
              type="number"
              label="Bandwidth Δf (c/µm)"
              :hint="bandwidthHint"
              persistent-hint
              @change="set('fwidth', $event)"
            />
            <v-text-field
              v-if="geometryData.sourceType === 'continuous' || !geometryData.sourceType"
              :value="geometryData.waveWidth"
              class="mt-3"
              outlined
              dense
              type="number"
              label="Turn-on time (µm/c)"
              hint="Smooth ramp at the start (0 = abrupt). A few periods (e.g. 5) avoid spurious frequencies"
              persistent-hint
              @change="set('waveWidth', $event)"
            />

            <template v-if="geometryData.sourceType === 'gaussian_beam'">
              <v-row dense class="mt-3">
                <v-col cols="6">
                  <v-text-field
                    :value="geometryData.beamWaist"
                    outlined
                    dense
                    type="number"
                    label="Beam waist w₀ (µm)"
                    hint="Radius of the beam at its focus (≥ λ/2)"
                    persistent-hint
                    @change="set('beamWaist', $event)"
                  />
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    :value="geometryData.beamAngle"
                    outlined
                    dense
                    type="number"
                    label="Direction (°)"
                    hint="0° = +x (right), 90° = +y (up)"
                    persistent-hint
                    @change="set('beamAngle', $event)"
                  />
                </v-col>
              </v-row>
              <p class="text-caption mt-2 mb-0">
                The beam is launched from a line through the source, perpendicular to its
                direction (leave the size below at 0 to span the whole cell). The focus is
                at the source position.
              </p>
            </template>

            <div class="text-caption mb-1 mt-4">
              Source size (µm) — 0 × 0 is a point source
            </div>
            <v-row dense>
              <v-col cols="6">
                <v-text-field
                  :value="(geometryData.waveSize || [0, 0])[0]"
                  outlined
                  dense
                  type="number"
                  label="Width X"
                  @change="setSize(0, $event)"
                />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  :value="(geometryData.waveSize || [0, 0])[1]"
                  outlined
                  dense
                  type="number"
                  label="Height Y"
                  @change="setSize(1, $event)"
                />
              </v-col>
            </v-row>
            <p class="text-caption mb-0">
              Tip: to excite a waveguide, use a vertical line (X = 0) about as tall as the guide.
              For a plane wave, make it span the whole cell and check the option below.
            </p>
            <v-checkbox
              :input-value="geometryData.integrated === 1"
              dense
              class="ma-0"
              label="Source extends into the PML"
              hint="Check when the source touches the absorbing border (e.g. plane waves)"
              persistent-hint
              @change="set('integrated', $event ? 1 : 0, false)"
            />
          </template>
        </v-col>
      </section>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>
<script>
import { mapActions, mapGetters } from "vuex";
import { SOURCE_TYPES } from "@/store/module-simulator/lib/monitors";
import { wavelengthToFrequency } from "@/store/module-simulator/lib/units";

export default {
  name: "SourceProperties",
  data: () => ({
    sourceTypes: SOURCE_TYPES,
    components: [
      { value: "Ez", text: "Ez (TM)" },
      { value: "Hz", text: "Hz (TE)" },
      { value: "Ex", text: "Ex" },
      { value: "Ey", text: "Ey" }
    ]
  }),
  computed: {
    ...mapGetters("simulator", ["geometryData"]),
    is3d() {
      return this.$store.state.simulator.is3d;
    },
    frequency() {
      return wavelengthToFrequency(Number(this.geometryData.waveLength));
    },
    frequencyHint() {
      return this.frequency > 0
        ? `f = 1/λ = ${this.frequency.toFixed(4)} c/µm`
        : "Required: must be greater than 0";
    },
    bandwidthHint() {
      const automatic = (0.2 * this.frequency).toFixed(4);
      return `Spectral width of the pulse. 0 = automatic (20% of f = ${automatic} c/µm)`;
    },
    typeHint() {
      return {
        continuous: "One wavelength, oscillating until the end (good for field pictures and videos)",
        gaussian: "Short pulse covering a band of wavelengths (spectra are always computed with a pulse)",
        gaussian_beam: "Focused beam with a Gaussian profile travelling in one direction"
      }[this.geometryData.sourceType || "continuous"];
    }
  },
  methods: {
    ...mapActions("simulator", ["updateGeometry"]),
    set(field, value, numeric = true) {
      this.updateGeometry([field, numeric ? Number(value) : value]);
    },
    setWavelength(value) {
      // frequency acompanha o comprimento de onda (o Meep usa f = 1/λ)
      this.set("waveLength", value);
      this.set("frequency", wavelengthToFrequency(Number(value)));
    },
    setSize(index, value) {
      const size = [...(this.geometryData.waveSize || [0, 0])];
      size[index] = Math.abs(Number(value) || 0);
      this.updateGeometry(["waveSize", size]);
    }
  }
};
</script>
